import * as React from 'react'
import * as Api from 'src/api'
import { useApi } from 'src/helpers/hooks'
import { type AuthenticatedHeaders } from 'src/hooks/auth/app'
import { type Option } from 'src/views/components/forms/formik/SelectInput'
import { SEMESTERS } from './constants'
import {
  courseListDefaultsToFormFields,
  courseSyllabusToFormValues,
  courseToFormValues,
  defaultCreateCourseStatusValue,
} from './mapModels'
import { type CourseAddFormValues } from './types'

const DEFAULT_NEW_COURSE_SEMESTERS = SEMESTERS.slice(0, 12)

export interface CourseAddPageData {
  readonly faculties: Api.getRegimeFacultiesOk | null | undefined
  readonly degrees: readonly Api.AdministratorListsType[] | null | undefined
  readonly courseDefinitions: Api.AdministratorCourseDefinition | null | undefined
  readonly courseTypeOptions: readonly Option[]
  readonly creditTypes: readonly Api.AdministratorListsType[] | null | undefined
  readonly initialValues: CourseAddFormValues
  readonly isLoading: boolean
}

export function useCourseAddPageData({ headers }: { readonly headers: AuthenticatedHeaders }): CourseAddPageData {
  const { data: faculties, isLoading: isFacultiesLoading } = useApi({
    endpoint: Api.getRegimeFaculties,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: degreesResponse, isLoading: isDegreesLoading } = useApi({
    endpoint: Api.getAdministratorListsDegrees,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })
  const degrees = degreesResponse

  const { data: courseDefinitions, isLoading: isDefinitionsLoading } = useApi({
    endpoint: Api.getAdministratorCourseDefinitions,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: courseTypesList, isLoading: isCourseTypesLoading } = useApi({
    endpoint: Api.getAdministratorListsCourseTypes,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: courseDefaultsResponse, isLoading: isCourseDefaultsLoading } = useApi({
    endpoint: Api.getAdministratorListsCourseDefaults,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: creditTypesResponse, isLoading: isCreditTypesLoading } = useApi({
    endpoint: Api.getAdministratorListsCreditTypes,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })
  const creditTypes = creditTypesResponse?.data

  const courseTypeOptions = React.useMemo(
    () => (courseTypesList ?? []).map((courseType) => ({ value: courseType.id, label: courseType.name })),
    [courseTypesList]
  )

  const initialValues = React.useMemo((): CourseAddFormValues => {
    const course = courseToFormValues(null)
    const defaultStatus = defaultCreateCourseStatusValue(courseDefinitions?.statuses)
    const defaultDegree = degrees?.[0]?.id != null ? String(degrees[0].id) : ''
    const defaultTypeId = courseTypesList?.[0]?.id != null ? String(courseTypesList[0].id) : ''
    const defaultCreditType = creditTypes?.[0]?.id != null ? String(creditTypes[0].id) : ''
    const fromListDefaults = courseListDefaultsToFormFields(courseDefaultsResponse?.data)
    return {
      ...course,
      userSem: DEFAULT_NEW_COURSE_SEMESTERS,
      ...(defaultStatus !== '' ? { status: defaultStatus } : {}),
      ...(course.degree === '' && defaultDegree !== '' ? { degree: defaultDegree } : {}),
      ...(course.typeId === '' && defaultTypeId !== '' ? { typeId: defaultTypeId } : {}),
      ...(course.type === '' && defaultCreditType !== '' ? { type: defaultCreditType } : {}),
      ...fromListDefaults,
      ...courseSyllabusToFormValues(null),
    }
  }, [courseDefinitions?.statuses, degrees, courseTypesList, creditTypes, courseDefaultsResponse?.data])

  const isLoading =
    isFacultiesLoading ||
    isDegreesLoading ||
    isDefinitionsLoading ||
    isCourseTypesLoading ||
    isCourseDefaultsLoading ||
    isCreditTypesLoading

  return {
    faculties,
    degrees,
    courseDefinitions,
    courseTypeOptions,
    creditTypes,
    initialValues,
    isLoading,
  }
}
